import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Upload, Search, RefreshCw, Sparkles, X, Wand2, Trash2, Download } from 'lucide-react';
import { getProducts, getStats, createProduct, updateProduct, deleteProduct, enrichProduct, syncVectors, importProducts, downloadTemplate } from '../api/client';
import { Button } from '../components/Button';
import type { Product } from '../types';
import styles from './Catalog.module.css';

export function CatalogPage() {
    const { clientId } = useParams<{ clientId: string }>();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [stats, setStats] = useState({ total: 0, pending: 0, validated: 0, synced: 0 });

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [saving, setSaving] = useState(false);
    const [enriching, setEnriching] = useState(false);

    // Import state
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importing, setImporting] = useState(false);

    // Delete state
    const [deleteConfirm, setDeleteConfirm] = useState<Product | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (clientId) {
            loadProducts();
            loadStats();
        }
    }, [clientId]);

    const loadProducts = async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const data = await getProducts(clientId, 1, 50, searchQuery);
            setProducts(data.products);
        } catch (err) {
            console.error('Failed to load products:', err);
        }
        setLoading(false);
    };

    const loadStats = async () => {
        if (!clientId) return;
        try {
            const data = await getStats(clientId);
            const pending = data.byStatus.find(s => s.validation_status === 'pending')?.count || 0;
            const validated = data.byStatus.find(s => s.validation_status === 'validated')?.count || 0;
            setStats({
                total: data.total,
                pending,
                validated,
                synced: data.synced || 0
            });
        } catch (err) {
            console.error('Failed to load stats:', err);
        }
    };

    const handleSync = async () => {
        if (!clientId) return;
        try {
            const result = await syncVectors(clientId);
            alert(result.message);
            loadStats();
            loadProducts();
        } catch (err) {
            console.error('Sync failed:', err);
            alert('Sync failed');
        }
    };

    const openAddModal = () => {
        setEditingProduct(null);
        setShowModal(true);
    };

    const openEditModal = (product: Product) => {
        setEditingProduct(product);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingProduct(null);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!clientId) return;

        const formData = new FormData(e.currentTarget);
        const data: Partial<Product> = {
            original_id: formData.get('original_id') as string,
            parfum_name: formData.get('parfum_name') as string,
            brand: formData.get('brand') as string,
            gender: formData.get('gender') as string,
            notes: formData.get('notes') as string,
            image_url: formData.get('image_url') as string,
            price: formData.get('price') as string,
            category: formData.get('category') as string,
            accord: formData.get('accord') as string,
            season: formData.get('season') as string,
            situation: formData.get('situation') as string,
            occasion: formData.get('occasion') as string,
            personality_tags: formData.get('personality_tags') as string,
            similar_to: formData.get('similar_to') as string,
            validation_status: formData.get('validation_status') as 'pending' | 'validated',
        };

        setSaving(true);
        try {
            if (editingProduct) {
                await updateProduct(editingProduct.id, data);
                alert('Produk berhasil diupdate!');
            } else {
                await createProduct(clientId, data);
                alert('Produk berhasil ditambahkan!');
            }
            closeModal();
            loadProducts();
            loadStats();
        } catch (err) {
            console.error('Save failed:', err);
            alert('Gagal menyimpan produk');
        }
        setSaving(false);
    };

    const handleEnrich = async () => {
        if (!editingProduct) return;

        setEnriching(true);
        try {
            const result = await enrichProduct(editingProduct.id);
            setEditingProduct(result.product);
            alert('AI Enrichment berhasil!');
        } catch (err) {
            console.error('Enrich failed:', err);
            alert('AI Enrichment gagal');
        }
        setEnriching(false);
    };

    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !clientId) return;

        setImporting(true);
        try {
            const result = await importProducts(clientId, file);
            alert(`Import selesai: ${result.imported} produk berhasil`);
            loadProducts();
            loadStats();
        } catch (err) {
            console.error('Import failed:', err);
            alert('Import gagal');
        }
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;

        setDeleting(true);
        try {
            await deleteProduct(deleteConfirm.id);
            setDeleteConfirm(null);
            loadProducts();
            loadStats();
        } catch (err) {
            console.error('Delete failed:', err);
            alert('Gagal menghapus produk');
        }
        setDeleting(false);
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <div>
                    <h1>📦 Product Catalog</h1>
                    <p>Kelola katalog produk parfum</p>
                </div>
                <div className={styles.headerActions}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleImport}
                        style={{ display: 'none' }}
                    />
                    <Button variant="outline" size="sm" onClick={downloadTemplate}>
                        <Download size={18} /> Template
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                        <Upload size={18} /> {importing ? 'Importing...' : 'Import'}
                    </Button>
                    <Button size="sm" onClick={openAddModal}>
                        <Plus size={18} /> Tambah Produk
                    </Button>
                </div>
            </header>

            {/* Stats */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <h3>Total Produk</h3>
                    <div className={styles.statValue}>{stats.total}</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Pending</h3>
                    <div className={styles.statValue} style={{ color: 'var(--warning)' }}>{stats.pending}</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Validated</h3>
                    <div className={styles.statValue} style={{ color: 'var(--success)' }}>{stats.validated}</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Published</h3>
                    <div className={styles.statValue} style={{ color: 'var(--accent)' }}>{stats.synced}</div>
                </div>
            </div>

            {/* Actions Bar */}
            <div className={styles.actionsBar}>
                <div className={styles.searchBox}>
                    <Search size={18} className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Cari produk..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && loadProducts()}
                    />
                </div>
                <div className={styles.actionButtons}>
                    <Button variant="outline" size="sm" onClick={loadProducts}>
                        <RefreshCw size={16} /> Refresh
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleSync}>
                        <Sparkles size={16} /> Publish to Search
                    </Button>
                </div>
            </div>

            {/* Products Table */}
            <div className={styles.tableContainer}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className="spinner" />
                        <p>Loading products...</p>
                    </div>
                ) : products.length === 0 ? (
                    <div className={styles.empty}>
                        <p>Belum ada produk. Import atau tambah produk baru.</p>
                    </div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Produk</th>
                                <th>Brand</th>
                                <th>Gender</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((product) => (
                                <tr key={product.id}>
                                    <td>
                                        <div className={styles.productCell}>
                                            <img
                                                src={product.image_url || 'https://via.placeholder.com/40x40/1a1a2e/8b5cf6?text='}
                                                alt={product.parfum_name}
                                                className={styles.productImage}
                                            />
                                            <div>
                                                <p className={styles.productName}>{product.parfum_name}</p>
                                                <p className={styles.productId}>{product.original_id}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{product.brand}</td>
                                    <td><span className={styles.genderBadge}>{product.gender}</span></td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${styles[product.validation_status]}`}>
                                            {product.validation_status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.rowActions}>
                                            <button className={styles.actionBtn} onClick={() => openEditModal(product)}>Edit</button>
                                            <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={() => setDeleteConfirm(product)}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className={styles.modalOverlay} onClick={closeModal}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>{editingProduct ? '✏️ Edit Produk' : '➕ Tambah Produk'}</h2>
                            <button className={styles.modalClose} onClick={closeModal}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className={styles.modalForm}>
                            {/* AI Enrich Button */}
                            {editingProduct && (
                                <div className={styles.enrichBar}>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleEnrich}
                                        disabled={enriching}
                                    >
                                        <Wand2 size={16} /> {enriching ? 'Enriching...' : 'AI Enrich'}
                                    </Button>
                                    <span className={styles.enrichHint}>Isi otomatis dengan AI</span>
                                </div>
                            )}

                            <div className={styles.formSection}>
                                <h3>📋 Info Dasar</h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Kode (SKU) *</label>
                                        <input name="original_id" defaultValue={editingProduct?.original_id} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Nama Parfum *</label>
                                        <input name="parfum_name" defaultValue={editingProduct?.parfum_name} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Brand *</label>
                                        <input name="brand" defaultValue={editingProduct?.brand} required />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Gender *</label>
                                        <select name="gender" defaultValue={editingProduct?.gender || 'Unisex'} required>
                                            <option value="Pria">Pria</option>
                                            <option value="Wanita">Wanita</option>
                                            <option value="Unisex">Unisex</option>
                                        </select>
                                    </div>
                                </div>
                                <div className={styles.formGroup}>
                                    <label>Notes / Karakter Wangi *</label>
                                    <textarea name="notes" defaultValue={editingProduct?.notes} required rows={2} />
                                </div>
                            </div>

                            <div className={styles.formSection}>
                                <h3>🌸 Enrichment Data</h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Category</label>
                                        <input name="category" defaultValue={editingProduct?.category} placeholder="Fresh, Woody, Floral..." />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Accord</label>
                                        <input name="accord" defaultValue={editingProduct?.accord} placeholder="Woody Aromatic..." />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Season</label>
                                        <input name="season" defaultValue={editingProduct?.season} placeholder="All Season, Summer..." />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Situation</label>
                                        <input name="situation" defaultValue={editingProduct?.situation} placeholder="Office, Date Night..." />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Occasion</label>
                                        <input name="occasion" defaultValue={editingProduct?.occasion} placeholder="Daily, Special Event..." />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Personality Tags</label>
                                        <input name="personality_tags" defaultValue={editingProduct?.personality_tags} placeholder="Elegant, Bold..." />
                                    </div>
                                </div>
                                <div className={styles.formGroup}>
                                    <label>Similar To</label>
                                    <input name="similar_to" defaultValue={editingProduct?.similar_to} placeholder="Dior Sauvage, Bleu de Chanel..." />
                                </div>
                            </div>

                            <div className={styles.formSection}>
                                <h3>📷 Media & Info</h3>
                                <div className={styles.formGrid}>
                                    <div className={styles.formGroup}>
                                        <label>Image URL</label>
                                        <input name="image_url" defaultValue={editingProduct?.image_url} placeholder="https://..." />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Harga</label>
                                        <input name="price" defaultValue={editingProduct?.price} placeholder="150000" />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label>Status</label>
                                        <select name="validation_status" defaultValue={editingProduct?.validation_status || 'pending'}>
                                            <option value="pending">Pending</option>
                                            <option value="validated">Validated</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.modalFooter}>
                                <Button type="button" variant="outline" onClick={closeModal}>Batal</Button>
                                <Button type="submit" disabled={saving}>
                                    {saving ? 'Menyimpan...' : '💾 Simpan'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className={styles.modalOverlay} onClick={() => setDeleteConfirm(null)}>
                    <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.deleteModalIcon}>🗑️</div>
                        <h3>Hapus Produk?</h3>
                        <p>Apakah Anda yakin ingin menghapus <strong>{deleteConfirm.parfum_name}</strong>?</p>
                        <p className={styles.deleteWarning}>Tindakan ini tidak dapat dibatalkan.</p>
                        <div className={styles.deleteModalActions}>
                            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                                Batal
                            </Button>
                            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                                {deleting ? 'Menghapus...' : 'Ya, Hapus'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
